import { msg } from '@lingui/core/macro';
import { OverlayReadinessStepId } from 'modules/overlay-v2/domain/interfaces/readiness';
import { MessageDescriptor } from 'shared/i18n';

/**
 * Все подписи чеклиста подготовки — в одном месте и без слов из мира разработки.
 * Экран видит человек с камерой и ноутбуком в спортзале: «журнал матча» вместо
 * `match_chronology`, «пульт» вместо realtime-канала, «логотипы и фото» вместо кэша.
 *
 * Подписи — дескрипторы Lingui: на уровне модуля язык ещё не известен, переводят их
 * `buildReadinessSteps` и компоненты чеклиста в момент сборки.
 */

export const READINESS_TITLE = msg`Подготовка трансляции`;
export const READINESS_READY_TITLE = msg`Всё готово`;
export const READINESS_RETRY_BUTTON = msg`Повторить`;

/** Заголовки шагов — существительные: одинаково читаются в любом статусе. */
export const STEP_TITLES: Record<OverlayReadinessStepId, MessageDescriptor> = {
  link: msg`Ссылка на трансляцию`,
  subscription: msg`Доступ к трансляции`,
  device: msg`Связь с устройством`,
  match: msg`Запущенный матч`,
  matchData: msg`Данные матча`,
  lineups: msg`Составы команд`,
  chronology: msg`Журнал матча`,
  matchTime: msg`Время и период`,
  template: msg`Оформление табло`,
  boards: msg`Табло в оформлении`,
  assets: msg`Логотипы и фото игроков`,
  remote: msg`Связь с пультом`,
};

/** Что делает шаг, пока крутится. */
export const STEP_RUNNING_DETAILS: Partial<Record<OverlayReadinessStepId, MessageDescriptor>> = {
  subscription: msg`Проверяем, оплачена ли трансляция`,
  device: msg`Ищем устройство из ссылки`,
  match: msg`Ищем матч на устройстве`,
  matchData: msg`Забираем счёт, фолы и составы`,
  lineups: msg`Смотрим, кто вышел на матч`,
  chronology: msg`Открываем журнал событий матча`,
  matchTime: msg`Сверяем секундомер и номер тайма`,
  template: msg`Загружаем оформление турнира`,
  boards: msg`Проверяем, есть ли что показывать`,
  assets: msg`Заранее скачиваем картинки`,
  remote: msg`Ждём отклик от приложения`,
};

/** Подсказки «что сделать» — показываются только у жёлтых и красных шагов. */
export const STEP_HINTS = {
  subscriptionMissing: msg`Трансляция входит в платную подписку — напишите в поддержку ImGame, её включат.`,
  matchWaiting: msg`Запустите матч в мобильном приложении ImGame — мы подхватим его сами, перезагружать страницу не нужно. Если матч уже идёт, проверьте, что в приложении выбрано то же устройство, что и в этой ссылке.`,
  noLineups: msg`В приложении никого не отметили вышедшим на матч. Табло с составами показать не получится, остальные будут работать.`,
  templateMissing: msg`Откройте «Турниры» → нужный турнир → вкладка «Основное» → поле «Шаблон» и выберите оформление.`,
  noBoards: msg`Откройте «Шаблоны» → это оформление и добавьте табло — показывать пока нечего.`,
  someImagesFailed: msg`Часть картинок не открылась. Табло покажем, но на их местах будет пусто.`,
  remoteTimedOut: msg`Пульт не отозвался. Данные будем запрашивать сами каждые 3 секунды — табло продолжит работать, но обновляться будет чуть медленнее.`,
  networkRetry: msg`Проверьте интернет — мы сами повторим попытку.`,
};

/** Ошибки, которые приходят от сервера, показываем человеку с понятной обёрткой. */
export const STEP_FAILURE_PREFIX: Partial<Record<OverlayReadinessStepId, MessageDescriptor>> = {
  subscription: msg`Не удалось проверить подписку`,
  device: msg`Не удалось связаться с устройством`,
  template: msg`Не удалось загрузить оформление`,
  matchData: msg`Не удалось загрузить данные матча`,
  chronology: msg`Не удалось открыть журнал матча`,
  matchTime: msg`Не удалось сверить время матча`,
  assets: msg`Не удалось скачать картинки`,
};

/** Шаги, которые не держат показ табло. */
export const NON_BLOCKING_STEPS: OverlayReadinessStepId[] = ['remote'];

/**
 * Шаги, которые бессмысленно повторять. Ссылку чинят правкой адреса — это перезагружает
 * страницу целиком, поэтому кнопка «Повторить» и обратный отсчёт здесь только сбивают
 * с толку. Остальные шаги повторять стоит: подписку включает поддержка, оформление
 * выбирают в соседней вкладке, а сеть просто моргает — всё это подхватывается повтором.
 */
export const NON_RETRYABLE_STEPS: OverlayReadinessStepId[] = ['link'];

/* ---------- Тайминги ---------- */

/** Сколько ждём связь с пультом, прежде чем пропустить шаг с предупреждением. */
export const REMOTE_CONNECTION_TIMEOUT_MS = 15_000;

/**
 * Сколько чеклист держится на экране после того, как всё сошлось: успеваем показать
 * «Всё готово» и проиграть уход, а не подменить экран рывком посреди эфира.
 */
export const READINESS_EXIT_DURATION_MS = 700;

/**
 * Сколько переключение матча может готовиться под знаком ImGame, прежде чем выйдет чеклист.
 * Отсчёт идёт от смены матча; ожидание запуска матча в приложении сюда тоже входит.
 */
export const MATCH_SWITCH_CHECKLIST_TIMEOUT_MS = 90_000;

/** Подпись знака ImGame при переключении матча — для скринридера, на экране её нет. */
export const MATCH_SWITCH_LOADER_LABEL = msg`Загружаем следующий матч`;
